export const SUPPORTED_CURRENCIES = ['COP', 'USD'] as const

export type Currency = (typeof SUPPORTED_CURRENCIES)[number]

export const DEFAULT_CURRENCY: Currency = 'COP'

export const CURRENCY_TRANSLATION_KEYS: Readonly<Record<Currency, string>> = {
  COP: 'currencies.COP',
  USD: 'currencies.USD'
}

export const CURRENCY_DISPLAY_FRACTION_DIGITS: Readonly<Record<Currency, number>> = {
  COP: 0,
  USD: 2
}

export function isSupportedCurrency(value: unknown): value is Currency {
  return typeof value === 'string'
    && (SUPPORTED_CURRENCIES as readonly string[]).includes(value)
}

export function normalizeCurrency(value: unknown): Currency | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  return isSupportedCurrency(normalized) ? normalized : null
}

export function currencyDisplayFractionDigits(currency: Currency) {
  return CURRENCY_DISPLAY_FRACTION_DIGITS[currency]
}

export function currencySelectOptions(translate: (key: string) => string) {
  return SUPPORTED_CURRENCIES.map(value => ({
    label: translate(CURRENCY_TRANSLATION_KEYS[value]),
    value
  }))
}

export interface FormatCurrencyOptions {
  /** 'symbol' (default, e.g. "$130.000") or 'code' (e.g. "COP 130.000", used in financial reports). */
  currencyDisplay?: 'symbol' | 'code'
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

/**
 * Single source of truth for rendering monetary amounts across the app (cart, POS sales, pricing, refunds, reports).
 * Never use this for values sent to the payment gateway or third-party integrations — those keep their own
 * untouched numeric/string contracts.
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string | null | undefined,
  locale: string,
  options: FormatCurrencyOptions = {}
): string {
  if (!currency) return String(amount)

  const supportedCurrency = normalizeCurrency(currency)
  const fractionDigits = supportedCurrency ? currencyDisplayFractionDigits(supportedCurrency) : 2

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: options.currencyDisplay ?? 'symbol',
    minimumFractionDigits: options.minimumFractionDigits ?? fractionDigits,
    maximumFractionDigits: options.maximumFractionDigits ?? fractionDigits
  }).format(amount)
}
